import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  GuestGetBookedMeetingResponseV2,
  GuestGetBookedMeetingResponseV2Interface,
} from '@vendasta/meetings';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { BookMeNowStoreService } from '../book-me-now-store.service';
import {
  buildDisplayTitle,
  extractEventTypeName,
  findBatchMeetingById,
  formatDate,
  formatNames,
  formatTimeRange,
  isBatchBooking,
  isInPersonMeeting,
} from '../util';

type CancelConfirmationVm = {
  meetingInfo: GuestGetBookedMeetingResponseV2;
  displayTitle: string;
  hostNamesText: string;
  dateText: string;
  timeRangeText: string;
  isInPerson: boolean;
  timezoneZoneText: string;
  bookingUrl: string;
};

type GuestBookedMeetingV2WithExtensions = GuestGetBookedMeetingResponseV2 & {
  timezone?: string | { zone?: string };
  timeZone?: { id?: string };
};

@Component({
  selector: 'app-cancel-confirmation-page',
  templateUrl: './cancel-confirmation.component.html',
  styleUrls: ['./cancel-confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CancelConfirmationPageComponent implements OnInit {
  meetingId!: string;
  meetingInfo$: Observable<GuestGetBookedMeetingResponseV2>;
  cancelConfirmationVm$: Observable<CancelConfirmationVm | null>;

  constructor(
    private readonly route: ActivatedRoute,
    public readonly bookMeNowStoreService: BookMeNowStoreService
  ) {}

  ngOnInit(): void {
    this.meetingId = this.route.snapshot.params.meetingId;

    this.meetingInfo$ = this.bookMeNowStoreService
      .getGuestBookedMeetingV2({ id: this.meetingId })
      .pipe(
        catchError((err) => {
          this.bookMeNowStoreService.handleMeetingError(err, 'cancel');
          return of(null);
        })
      );

    this.cancelConfirmationVm$ = this.meetingInfo$.pipe(
      map((meetingInfo) => {
        if (!meetingInfo) {
          return null;
        }

        const meetingDetails =
          meetingInfo as GuestBookedMeetingV2WithExtensions;
        const timezoneRegion =
          typeof meetingDetails.timezone === 'string'
            ? meetingDetails.timezone
            : meetingDetails.timeZone?.id;
        const timezoneZoneText =
          typeof meetingDetails.timezone === 'object'
            ? meetingDetails.timezone?.zone || ''
            : '';

        const isInPerson = isInPersonMeeting(meetingInfo.meetingLocationType);

        const responseWithBatch =
          meetingInfo as unknown as GuestGetBookedMeetingResponseV2Interface;
        const isBatch = isBatchBooking(responseWithBatch);

        // When the URL identifies one meeting in a batch, show only that
        // meeting's event-type name instead of comma-joining the whole batch.
        const matchedBatchMeeting = isBatch
          ? findBatchMeetingById(responseWithBatch.meetings, this.meetingId)
          : null;

        const displayTitle = matchedBatchMeeting
          ? extractEventTypeName(matchedBatchMeeting.title || '')
          : buildDisplayTitle(
              meetingInfo.title || '',
              isBatch,
              responseWithBatch.meetings || []
            );

        const bookingUrl = meetingInfo.bookingUrl || '';

        return {
          meetingInfo,
          displayTitle,
          hostNamesText: formatNames(meetingInfo.hostUserNames || []),
          dateText: formatDate(meetingInfo.startTime),
          timeRangeText: formatTimeRange(
            meetingInfo.startTime,
            meetingInfo.endTime,
            timezoneRegion
          ),
          isInPerson,
          timezoneZoneText,
          bookingUrl,
        };
      }),
      shareReplay(1)
    );
  }

  bookNow(bookingUrl: string): void {
    if (bookingUrl) {
      window.location.href = bookingUrl;
    }
  }
}
